import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Game, Member, Room } from '../../types/game'
import { SeasonIcon } from '../shared/ResourceIcon'
import { PlayerMarker } from '../shared/PlayerMarker'
import { sendChatMessage } from '../../api/rooms'
import { formatGameMessage } from '../../utils/formatMessage'

interface LogPanelProps {
  game: Game
  room: Room
  memberId: string
  onOpenMenu: () => void
}

type RenderedMessage = {
  id: string
  sortKey: string
  content: ReactNode
}

// the log renders a sliding window over the full history so the DOM stays
// small: LOG_PAGE newest rows in live mode, at most LOG_WINDOW while
// scrolling back (each pull of LOG_PAGE older rows drops as many newest ones)
const LOG_PAGE = 25
const LOG_WINDOW = 75
const SCROLL_EDGE_PX = 20

export function LogPanel({ game, room, memberId, onOpenMenu }: LogPanelProps) {
  const [chatText, setChatText] = useState('')
  const [recipientIndex, setRecipientIndex] = useState(0)
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const privateRecipientCandidates = useMemo(() => {
    const message = chatText.trimStart()
    if (!message.startsWith('@')) {
      return []
    }

    const mentionQuery = message.slice(1)
    if (mentionQuery.includes(' ')) {
      return []
    }

    return room.members.filter(
      (candidate) =>
        candidate.id !== memberId &&
        !candidate.is_bot &&
        (!mentionQuery || candidate.alias.toLowerCase().startsWith(mentionQuery.toLowerCase()))
    )
  }, [chatText, memberId, room.members])

  const mergedMessages = useMemo(() => {
    const allMembers = [...room.left_members, ...room.members]
    const aliasByColor = Object.fromEntries(
      allMembers
        .filter((member) => member.color !== null)
        .map((member) => [member.color as string, member.alias])
    )
    const aliasById = Object.fromEntries(allMembers.map((member) => [member.id, member.alias]))
    const colorById = Object.fromEntries(allMembers.map((member) => [member.id, member.color]))
    const selfMember = room.members.find((member) => member.id === memberId) ?? null
    const selfColor = selfMember?.color ?? null
    const gameLogs: RenderedMessage[] = game.logs
      .filter((log) => log.target === null || log.target === selfColor)
      .map((log) => ({
        id: `game-${log.timestamp}`,
        sortKey: log.timestamp,
        content: (
          <div className="message">
            <SeasonIcon season={log.season} inverted />
            <div>{formatGameMessage(log.message, aliasByColor)}</div>
          </div>
        ),
      }))

    const chatLogs: RenderedMessage[] = (room.buffer ?? [])
      .filter((entry) => entry.type === 'chat')
      .filter((entry) => {
        const target = entry.payload.target as string | undefined
        return !target || target === memberId || entry.payload.user === memberId
      })
      .map((entry) => {
        const userId = entry.payload.user as string
        const color = colorById[userId]
        let messageText = String(entry.payload.text ?? '')
        for (const [aliasId, alias] of Object.entries(aliasById)) {
          messageText = messageText.split(`{${aliasId}}`).join(alias)
        }

        return {
          id: `chat-${entry.timestamp}-${userId}`,
          sortKey: entry.timestamp,
          content: (
            <div className="message">
              {color ? <PlayerMarker color={color} className="opponent-marker" /> : null}
              <div>
                <span style={{ color: 'var(--grey)' }}>{aliasById[userId] ?? 'Unknown'}:</span>
                {messageText.startsWith(' ') ? messageText : ` ${messageText}`}
              </div>
            </div>
          ),
        }
      })

    return [...gameLogs, ...chatLogs]
      .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
  }, [game.logs, memberId, room.buffer, room.left_members, room.members])

  // null = live mode (follow the newest messages); a number = browse mode,
  // the absolute index just past the last visible message
  const [browseEnd, setBrowseEnd] = useState<number | null>(null)
  const scrollAnchorRef = useRef<{ id: string; top: number } | null>(null)

  const visibleMessages = useMemo(() => {
    if (browseEnd === null) {
      return mergedMessages.slice(-LOG_PAGE)
    }
    return mergedMessages.slice(Math.max(0, browseEnd - LOG_WINDOW), browseEnd)
  }, [browseEnd, mergedMessages])

  // before the window shifts, remember where a row that survives the shift
  // sits on screen, so the scroll position can be restored after the render
  const captureScrollAnchor = (edge: 'first' | 'last') => {
    const container = messagesRef.current
    if (!container) return
    const rows = container.querySelectorAll<HTMLElement>('[data-message-id]')
    const row = edge === 'first' ? rows[0] : rows[rows.length - 1]
    if (!row) return
    scrollAnchorRef.current = {
      id: row.dataset.messageId as string,
      top: row.getBoundingClientRect().top,
    }
  }

  const handleMessagesScroll = () => {
    const container = messagesRef.current
    if (!container) return
    const nearTop = container.scrollTop < SCROLL_EDGE_PX
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < SCROLL_EDGE_PX

    if (nearTop) {
      if (browseEnd === null) {
        if (mergedMessages.length > LOG_PAGE) {
          captureScrollAnchor('first')
          setBrowseEnd(mergedMessages.length)
        }
      } else if (browseEnd - LOG_WINDOW > 0) {
        captureScrollAnchor('first')
        setBrowseEnd(Math.max(LOG_WINDOW, browseEnd - LOG_PAGE))
      }
      return
    }

    if (nearBottom && browseEnd !== null) {
      if (browseEnd >= mergedMessages.length) {
        setBrowseEnd(null)
      } else {
        captureScrollAnchor('last')
        setBrowseEnd(Math.min(mergedMessages.length, browseEnd + LOG_PAGE))
      }
    }
  }

  useLayoutEffect(() => {
    const container = messagesRef.current
    if (!container) return

    const anchor = scrollAnchorRef.current
    if (anchor) {
      scrollAnchorRef.current = null
      const row = container.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(anchor.id)}"]`)
      if (row) {
        container.scrollTop += row.getBoundingClientRect().top - anchor.top
        return
      }
    }

    if (browseEnd === null) {
      container.scrollTop = container.scrollHeight
    }
  }, [visibleMessages, browseEnd])

  useEffect(() => {
    if (!privateRecipientCandidates.length) {
      setRecipientIndex(0)
      return
    }

    setRecipientIndex((currentIndex) => Math.min(Math.max(currentIndex, 0), privateRecipientCandidates.length - 1))
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [privateRecipientCandidates])

  const applyPrivateRecipient = (recipient: Member) => {
    const message = chatText.trimStart()
    const remainder = message.includes(' ') ? message.split(' ', 2)[1] ?? '' : ''
    setChatText(remainder ? `@${recipient.alias} ${remainder.trimStart()}` : `@${recipient.alias} `)
    setRecipientIndex(0)
  }

  const handleSendChat = async () => {
    let message = chatText.trim()
    if (!message) {
      return
    }

    let target: string | undefined
    if (message.startsWith('@')) {
      const recipients = [...room.members]
        .filter((candidate) => candidate.id !== memberId && !candidate.is_bot)
        .sort((left, right) => right.alias.length - left.alias.length)

      for (const recipient of recipients) {
        const prefix = `@${recipient.alias}`
        if (message === prefix || message.startsWith(`${prefix} `)) {
          target = recipient.id
          message = message.slice(prefix.length).trimStart()
          break
        }
      }
    }

    if (!message) {
      return
    }

    try {
      await sendChatMessage(room.id, { member_id: memberId, text: message, ...(target ? { target } : {}) })
      setChatText('')
    } catch {
      // keep legacy behavior of silently ignoring send failures here
    }
  }

  return (
    <div className="log tableau">
      <div id="messages" className="messages" ref={messagesRef} onScroll={handleMessagesScroll}>
        {visibleMessages.map((message) => (
          <div key={message.id} data-message-id={message.id}>{message.content}</div>
        ))}
      </div>
      <div className="chat">
        <div className="chat-input-stack">
          <div className={`chat-recipient-list${privateRecipientCandidates.length ? '' : ' hidden'}`}>
            {privateRecipientCandidates.map((candidate, index) => (
              <button
                key={candidate.id}
                type="button"
                className={`chat-recipient-option${index === recipientIndex ? ' selected' : ''}`}
                onClick={() => applyPrivateRecipient(candidate)}
              >
                {candidate.alias}
              </button>
            ))}
          </div>
          <input
            ref={inputRef}
            id="chat-message"
            className="input chat-message"
            placeholder="Type a message..."
            value={chatText}
            onChange={(event) => setChatText(event.target.value)}
            onKeyDown={(event) => {
              if (!privateRecipientCandidates.length) {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void handleSendChat()
                }
                return
              }

              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setRecipientIndex((currentIndex) => (currentIndex + 1) % privateRecipientCandidates.length)
                return
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault()
                setRecipientIndex((currentIndex) => (currentIndex - 1 + privateRecipientCandidates.length) % privateRecipientCandidates.length)
                return
              }

              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault()
                applyPrivateRecipient(privateRecipientCandidates[recipientIndex % privateRecipientCandidates.length])
              }
            }}
          />
        </div>
        <div id="chat-send" className="chat-send" onClick={() => void handleSendChat()}>
          Send
        </div>
        <div id="chat-menu-button" className="chat-menu-button lobby-start" onClick={onOpenMenu}>
          Menu
        </div>
      </div>
    </div>
  )
}
